import { Card, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { getMemberOrderListV2 } from 'apis/oms';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { PageQuery, UnwrapPromise } from 'types/base';
import { UmsMember } from 'types/ums';
import formatTime from 'utils/formatTime';
import {
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
  findLabelByValue,
} from 'commons/options';
import showTotal from 'components/show-total';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import LinkButton from 'components/link-button';
import { ShoppingCart } from 'lucide-react';
import { ORDER_CREATED_FROM_OPTION_LIST } from 'constant/order-create-source';
import { getOrderStatusConfig } from 'constant/oms-order-status';

type MemberOrderListProps = {
  memberId: UmsMember['id'];
};

type UmsMemberOrder = UnwrapPromise<
  ReturnType<typeof getMemberOrderListV2>
>['list'][number];

const MemberOrderList = ({ memberId }: MemberOrderListProps) => {
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<UmsMemberOrder>();

  const { orderStatusOptions } = useAppSelector(selectGlobalInfo);

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);

        const { list, total } = await getMemberOrderListV2({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal],
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const getColumns = useCallback(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.dwawuQNUEi}></Trans>,
        dataIndex: 'id',
        key: 'id',
        render: (id: number) => {
          return <LinkButton href={`/oms/order-view/${id}`}>{id}</LinkButton>;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.sales_product_id}></Trans>,
        dataIndex: 'orderItems',
        key: 'order-id-list',
        render: (orderItemList: UmsMemberOrder['orderItems']) => {
          return (
            <div>
              {orderItemList.map(({ productId }) => {
                const href = `/pms/product-edit/${productId}`;
                return (
                  <div key={productId}>
                    <a href={href}>{productId}</a>
                  </div>
                );
              })}
            </div>
          );
        },
      },

      {
        title: <Trans i18nKey={LOCALS.product_name}></Trans>,
        dataIndex: 'orderItems',
        key: 'product-name-list',
        render: (orderItemList: UmsMemberOrder['orderItems']) => {
          return (
            <div>
              {orderItemList.map(
                ({
                  productId,
                  product: { name, note, color, stockStatus },
                }) => {
                  return (
                    <div key={productId}>
                      {name} {note} {color}{' '}
                      {stockStatus
                        ? findLabelByValue(
                            stockStatus,
                            PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
                          )
                        : ''}
                    </div>
                  );
                },
              )}
            </div>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: UmsMemberOrder['createTime']) => {
          return formatTime(createTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
        dataIndex: 'payAmount',
        key: 'payAmount',
        render: (
          payAmount: UmsMemberOrder['payAmount'],
          {
            orderItems: [{ actualCurrency }],
            payAmountActualCurrency,
          }: UmsMemberOrder,
        ) => {
          if (actualCurrency && payAmountActualCurrency !== null) {
            return `${actualCurrency} ${payAmountActualCurrency.toLocaleString()}`;
          }

          return `JPY ${payAmount.toLocaleString()}`;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_from}></Trans>,
        dataIndex: 'createdFrom',
        key: 'createdFrom',
        render: (createdFrom: UmsMemberOrder['createdFrom']) => {
          return findLabelByValue(createdFrom, ORDER_CREATED_FROM_OPTION_LIST);
        },
      },

      {
        title: <Trans i18nKey={LOCALS.staff}></Trans>,
        dataIndex: 'staffName',
        key: 'staffName',
      },
      {
        title: <Trans i18nKey={LOCALS.order_status}></Trans>,
        dataIndex: 'status',
        key: 'status',
        render: (status: UmsMemberOrder['status']) => {
          return (
            <Tag color={getOrderStatusConfig(status).color}>
              {findLabelByValue(status, orderStatusOptions)}
            </Tag>
          );
        },
      },
    ];
  }, [orderStatusOptions]);

  const columns: ColumnsType<UmsMemberOrder> = useMemo(
    () => getColumns(),
    [getColumns],
  );

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <ShoppingCart className="w-5 h-5" />
        <Trans i18nKey={LOCALS.order_list} />
      </div>
      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberOrderList;
